import type { Observable } from "@legendapp/state";
import { observable, observe } from "@legendapp/state";
import { scormU } from "../util/scorm/scormUnified";
import { initialState } from "../data/initialState";
import type {
  StoreModelType,
  ActType,
  ActResultType,
} from "../types/globalStore.types";

export type { StoreModelType, ActType, ActResultType };

export const globalStore$ = observable(
  initialState,
) as Observable<StoreModelType>;

export const setCurrentScreen = (currentScreen: string) => {
  globalStore$.currentScreen.set(currentScreen);
};

export const completeStep = (actId: string, value: ActResultType) => {
  const acts = globalStore$.acts.get();
  const index = acts.findIndex((act) => act.id === actId);
  if (index === -1) return false;
  globalStore$.acts[index].result.set(value);
  return true;
};

export const getCourseCompleted = (): boolean => {
  return globalStore$.acts.get().every((act) => act.result === 2);
};

export const getRawPoints = (): number => {
  const acts = globalStore$.acts.get();
  if (acts.length === 0) return 0;
  const passed = acts.filter((act) => act.result === 2).length;
  return (passed * 100) / acts.length;
};

export const saveStoreToLMS = () => {
  if (globalStore$.hasFinalized.get()) {
    console.log("[Store] saveStoreToLMS: episodio ya finalizado, saltando guardado");
    return;
  }

  if (!scormU.isConnected()) {
    console.warn("[Store] saveStoreToLMS: SCORM no conectado, saltando guardado");
    return;
  }

  console.log("[Store] Guardando datos al LMS...");

  const currentScreen = globalStore$.currentScreen.get();
  const acts = globalStore$.acts.get();
  const hasStarted = globalStore$.hasStarted.get();

  // Navegación -> bookmark; datos de actos -> suspend_data (JSON)
  scormU.setBookmark(currentScreen);
  scormU.setSuspendData(JSON.stringify({ acts, hasStarted }));

  scormU.setScore({ raw: getRawPoints(), min: 0, max: 100 });

  if (getCourseCompleted()) {
    scormU.setCompletion("passed");
    scormU.setSuccess("passed");
    scormU.set("exit", "logout");
  } else {
    scormU.setCompletion("incomplete");
    scormU.set("exit", "suspend");
  }

  const saveSuccess = scormU.save();
  console.log(saveSuccess ? "[Store] ✓ Datos guardados" : "[Store] ✗ Error al guardar");
};

export const finalizeEpisode = () => {
  if (globalStore$.hasFinalized.get()) {
    console.log("[Store] finalizeEpisode: episodio ya finalizado, ignorando");
    return;
  }

  console.log("[Store] Finalizando episodio...");

  const acts = globalStore$.acts.get();
  globalStore$.hasFinalized.set(true);

  if (!scormU.isConnected()) {
    console.warn("[Store] finalizeEpisode: SCORM no conectado, solo se marca localmente");
    return;
  }

  scormU.setBookmark("EP_1_CIERRE");
  scormU.setSuspendData(JSON.stringify({ acts, hasFinalized: true, hasStarted: true }));
  scormU.setScore({ raw: 100, min: 0, max: 100 });
  scormU.setCompletion("completed");
  scormU.setSuccess("passed");

  const saveSuccess = scormU.save();
  console.log(saveSuccess ? "[Store] ✓ Episodio finalizado y guardado" : "[Store] ✗ Error al finalizar episodio");
};

export function hydrateFromSCORM() {
  if (!scormU.isConnected()) {
    console.warn("[Store] SCORM no conectado, iniciando con valores por defecto");
    return;
  }

  console.log("[Store] Hidratando desde SCORM...");
  applyHydratedData(scormU.get("bookmark"), scormU.get("suspendData"));
  console.log("[Store] Estado hidratado:", globalStore$.get());
}

function applyHydratedData(bookmark?: string, suspendRaw?: string) {
  if (bookmark) {
    globalStore$.currentScreen.set(bookmark);
  }

  if (!suspendRaw) return;

  try {
    const parsed = JSON.parse(suspendRaw) as {
      acts?: ActType[];
      hasFinalized?: boolean;
      hasStarted?: boolean;
    };
    if (!Array.isArray(parsed.acts)) return;

    // Conservar el orden y el conjunto de actos declarados en navigation.json;
    // tomar de suspend_data solo el result de cada uno.
    globalStore$.acts.set(
      initialState.acts.map((declared) => {
        const hydrated = parsed.acts?.find((act) => act && act.id === declared.id);
        return hydrated ? { ...declared, result: hydrated.result } : declared;
      }),
    );

    if (typeof parsed.hasFinalized === "boolean") {
      globalStore$.hasFinalized.set(parsed.hasFinalized);
    }

    if (typeof parsed.hasStarted === "boolean") {
      globalStore$.hasStarted.set(parsed.hasStarted);
    }
  } catch (e) {
    console.error("[Store] ✗ Error parseando suspend_data, usando valores por defecto", e);
  }
}

export function hydrateFromRaw(data: { bookmark?: string; suspendData?: string }) {
  console.log("[Store] Hidratando desde raw:", data);
  applyHydratedData(data.bookmark, data.suspendData);
  console.log("[Store] Resultado:", globalStore$.get());
}

// Función CRÍTICA para el cierre de sesión
// SuccessFactors requiere que la secuencia de cierre se ejecute desde la ventana padre
export function robustLMSExit() {
  if (!scormU.isConnected()) return;

  if (window.opener) {
    window.opener.postMessage("SCORM_FINISH", "*");
    console.log("[Store] postMessage SCORM_FINISH enviado al padre");
  } else {
    scormU.quit(true);
    console.log("[Store] Fallback: quit() directo");
  }
}

function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: any;
  return function (this: any, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

const debouncedSave = debounce(saveStoreToLMS, 200);

// Un solo observer: cualquier cambio en el store se persiste (debounced)
observe(() => {
  globalStore$.get();
  debouncedSave();
});

(window as any).store = {
  globalStore$,
  setCurrentScreen,
  completeStep,
  getCourseCompleted,
  getRawPoints,
  saveStoreToLMS,
  finalizeEpisode,
  hydrateFromSCORM,
  hydrateFromRaw,
  robustLMSExit,
};
